import { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PaperPlaneRight, ArrowLeft } from '@phosphor-icons/react';

function ConversationList({ conversations, activeId, onSelect, userId }) {
  if (!conversations.length) {
    return <p className="p-6 text-center text-muted-foreground text-sm font-sans">No conversations yet</p>;
  }
  return (
    <div className="divide-y divide-border" data-testid="conversations-list">
      {conversations.map(conv => {
        const otherName = conv.buyer_id === userId ? conv.seller_name : conv.buyer_name;
        const isActive = conv.conversation_id === activeId;
        return (
          <button
            key={conv.conversation_id}
            onClick={() => onSelect(conv.conversation_id)}
            className={`w-full text-left p-4 hover:bg-muted/50 transition-colors ${isActive ? 'bg-muted/80' : ''}`}
            data-testid={`conv-${conv.conversation_id}`}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold font-sans truncate">{otherName || 'Golfer'}</p>
              <span className="text-[10px] text-muted-foreground font-sans">
                {new Date(conv.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
            <p className="text-xs text-[#D4AF37] font-sans truncate mt-0.5">{conv.listing_title}</p>
            {conv.last_message && <p className="text-xs text-muted-foreground font-sans truncate mt-1">{conv.last_message}</p>}
          </button>
        );
      })}
    </div>
  );
}

function ChatView({ conversationId, userId, onBack }) {
  const [messages, setMessages] = useState([]);
  const [conv, setConv] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const { data } = await api.get(`/conversations/${conversationId}/messages`);
      setMessages(data.messages);
      setConv(data.conversation);
    } catch {}
  }, [conversationId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || !conv) return;
    setSending(true);
    try {
      const receiverId = conv.buyer_id === userId ? conv.seller_id : conv.buyer_id;
      const { data } = await api.post('/messages', {
        listing_id: conv.listing_id,
        receiver_id: receiverId,
        text: text.trim()
      });
      setMessages(prev => [...prev, data]);
      setText('');
    } catch {} finally { setSending(false); }
  };

  const otherName = conv ? (conv.buyer_id === userId ? conv.seller_name : conv.buyer_name) : '';

  return (
    <div className="flex flex-col h-full" data-testid="chat-view">
      {/* Header */}
      <div className="border-b border-border p-4 flex items-center gap-3">
        <button onClick={onBack} className="lg:hidden p-1 text-muted-foreground hover:text-foreground" data-testid="chat-back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="min-w-0">
          <p className="text-sm font-semibold font-sans truncate">{otherName || 'Golfer'}</p>
          {conv && <p className="text-xs text-[#D4AF37] font-sans truncate">{conv.listing_title}</p>}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map(msg => {
          const isMine = msg.sender_id === userId;
          return (
            <div key={msg.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.message_id}`}>
              <div className={`max-w-[75%] px-4 py-2.5 rounded-sm ${isMine ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                <p className="text-sm font-sans">{msg.text}</p>
                <p className={`text-[10px] mt-1 ${isMine ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                  {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-border p-3 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
          placeholder="Type a message..."
          className="flex-1 bg-transparent border border-border rounded-sm focus:border-primary focus:outline-none focus:ring-0 px-3 py-2 text-sm font-sans"
          data-testid="chat-input"
        />
        <button
          onClick={handleSend}
          disabled={sending || !text.trim()}
          className="px-3 py-2 bg-primary text-primary-foreground rounded-sm hover:bg-[#083313] transition-colors disabled:opacity-40"
          data-testid="chat-send-btn"
        >
          <PaperPlaneRight size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await api.get('/conversations');
      setConversations(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  if (!user) return <Navigate to="/auth" replace />;

  if (loading) {
    return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background"><div className="animate-pulse w-8 h-8 rounded-full bg-muted" /></div>;
  }

  return (
    <div className="h-[calc(100vh-4rem)] bg-background flex" data-testid="messages-page">
      {/* Sidebar */}
      <div className={`w-full lg:w-80 border-r border-border flex flex-col ${activeConvId ? 'hidden lg:flex' : ''}`}>
        <div className="border-b border-border p-4">
          <h1 className="font-serif text-xl font-medium" data-testid="messages-title">Messages</h1>
        </div>
        <div className="flex-1 overflow-y-auto">
          <ConversationList conversations={conversations} activeId={activeConvId} onSelect={setActiveConvId} userId={user.user_id} />
        </div>
      </div>

      {/* Chat */}
      <div className={`flex-1 flex flex-col ${!activeConvId ? 'hidden lg:flex' : ''}`}>
        {activeConvId ? (
          <ChatView conversationId={activeConvId} userId={user.user_id} onBack={() => setActiveConvId(null)} />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-muted-foreground text-sm font-sans">Select a conversation</p>
          </div>
        )}
      </div>
    </div>
  );
}
